import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PartnerType } from 'legacy/lut/classes/models/PartnerType'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import { TRANSLATION_KEYS } from 'partner360/components/QBR/translations'
import { requestQbrDocument } from 'partner360/features/QBR/api/requestQbrDocument'
import { requestQbrDocumentPartners } from 'partner360/features/QBR/api/requestQbrDocumentPartners'
import { collapseTableRow } from 'partner360/helpers/financialTable/collapseTableRow'
import { expandTableRow } from 'partner360/helpers/financialTable/expandTableRow'
import { getAllParentsIds } from 'partner360/helpers/financialTable/getAllParentsIds'
import { IActionTableRowArgs } from 'partner360/models/actionTableRowArgs'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'
import { ISobCompetitor } from 'partner360/models/ISobCompetitor'
import { IFetchQbrDocumentResponse } from 'partner360/models/qbr/IFetchQbrDocumentResponse'
import { IQBRDocumentDetail } from 'partner360/models/qbr/IQBRDocumentDetail'
import { IQBRPartner } from 'partner360/models/qbr/IQBRPartners'
import { QBRRounding } from 'partner360/models/qbr/QBRRounding'

import { IQbrDocumentInitiative } from '../../models/qbr/iQbrDocumentInitiative'

import { createDraftQbr } from './api/createDraftQbr'
import { requestApplyChanges } from './api/requestApplyChanges'
import { requestFinalizeDocumentWithoutSavingChanges } from './api/requestFinalizeDocumentWithoutSavingChanges'
import { requestGeneratePptxDocument } from './api/requestGeneratePptxDocument'
import { requestResetData } from './api/requestResetData'
import { initialState } from './configs'
import { PARTNER360_QBR_NAMESPACE } from './constants'
import { IActionExpandCollapseSobCompetitorsTable } from './models/actionExpandCollapseSobCompetitorsTable'
import { openQbrDocumentReducer } from './reducers/openQbrDocumentReducer'

const QBRSlice = createSlice({
  name: PARTNER360_QBR_NAMESPACE,
  initialState,
  reducers: {
    changeRTM: (state, action: PayloadAction<PartnerType>) => {
      state.rtm = action.payload
    },
    setQbrEntryId: (state, action: PayloadAction<number>) => {
      state.entryId = action.payload
    },
    changeQBRDocumentName: (state, action: PayloadAction<string>) => {
      state.name = action.payload
    },
    setUserRole: (state, action: PayloadAction<PartnerType>) => {
      state.userRole = action.payload
    },
    changeQBRPartners: (state, action: PayloadAction<IQBRPartner[]>) => {
      state.documentPartners = action.payload
    },
    changeQBRNetNdp: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload) {
        state.documentSettings.netNdp = action.payload as ActualsValueTypes
      }
    },
    changeQBRCurrency: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload) {
        state.documentSettings.currency = action.payload
      }
    },
    changeQBRRounding: (state, action: PayloadAction<QBRRounding>) => {
      state.documentSettings.rounding = action.payload
    },
    changeQBROfferings: (state, action: PayloadAction<ProgramTypes[]>) => {
      state.documentSettings.offerings = action.payload
    },

    changeQBRLastStep: (state, action: PayloadAction<number>) => {
      state.lastStep = action.payload
    },
    changeDocumentFormData: (state, action: PayloadAction<IQBRDocumentDetail | null>) => {
      state.selectedDocument = action.payload
    },
    changeIsLegacyDocument: (state, action: PayloadAction<boolean>) => {
      state.isLegacyDocument = action.payload
    },
    changeQBRNetNdpOptions: (state, action: PayloadAction<string[]>) => {
      state.documentSettings.netNdpOptions = action.payload
    },
    changeQBRCurrencyOptions: (state, action: PayloadAction<string[]>) => {
      state.documentSettings.currencyOptions = action.payload
    },
    removeInitiative: (state, action: PayloadAction<number>) => {
      state.lastModified = new Date()
      state.isSelectedDocumentChanged = true
      const selectedDocument = state.selectedDocument
      const initiativeId = action.payload
      if (selectedDocument) {
        const initiativeIndex = selectedDocument.KeyInitiatives.findIndex(
          (it) => it.EntryId === initiativeId
        )

        if (initiativeIndex !== -1) {
          selectedDocument.KeyInitiatives.splice(initiativeIndex, 1)
        }
      }
    },
    changeInitiative: (state, action: PayloadAction<IQbrDocumentInitiative>) => {
      state.lastModified = new Date()
      state.isSelectedDocumentChanged = true
      const selectedDocument = state.selectedDocument
      const newInitiative = action.payload
      if (selectedDocument) {
        const initiativeIndex = selectedDocument.KeyInitiatives.findIndex(
          (it) => it.EntryId === newInitiative.EntryId
        )

        if (initiativeIndex !== -1) {
          selectedDocument.KeyInitiatives[initiativeIndex] = newInitiative
        }
      }
    },
    addInitiatives: (state, action: PayloadAction<IQbrDocumentInitiative[]>) => {
      state.lastModified = new Date()
      state.isSelectedDocumentChanged = true
      const selectedDocument = state.selectedDocument
      const initiatives = action.payload
      if (selectedDocument) {
        selectedDocument.KeyInitiatives.push(...initiatives)
      }
    },
    resetQbrPptxError: (state) => {
      state.pptxReportState.error = null
    },
    changeUnpopulatedMessages: (state, action: PayloadAction<string[]>) => {
      state.unpopulatedFieldsMessages = action.payload
    },
    openQBRDocument: (state, action: PayloadAction<IFetchQbrDocumentResponse>) => {
      openQbrDocumentReducer(state, action.payload)
    },
    clearState: (state) => {
      return { ...initialState, rtm: state.rtm, userRole: state.userRole }
    },
    deleteQBRSettingsVersion: (state, action: PayloadAction<string>) => {
      state.documentPartners = state.documentPartners.filter(
        (partner) => partner.PartnerID !== action.payload
      )
    },
    changeIsSelectedDocumentChanged: (state, action: PayloadAction<boolean>) => {
      if (state.selectedDocument) {
        state.isSelectedDocumentChanged = action.payload
      }
    },
    expandPlanAchievementTableRow: (
      state,
      { payload: { keyField, keyFieldValue } }: PayloadAction<IActionTableRowArgs>
    ) => {
      if (state.selectedDocument?.PlanAchievement) {
        state.selectedDocument.PlanAchievement = state.selectedDocument.PlanAchievement.map(
          (el) => {
            if (el[keyField] === keyFieldValue) {
              return { ...el, IsExpanded: 1 }
            }
            return el
          }
        )
      }
    },
    collapsePlanAchievementTableRow: (
      state,
      { payload: { keyField, keyFieldValue, hierarchyField } }: PayloadAction<IActionTableRowArgs>
    ) => {
      if (state.selectedDocument?.PlanAchievement) {
        state.selectedDocument.PlanAchievement = state.selectedDocument.PlanAchievement.map(
          (el, index, arr) => {
            if (el[keyField] === keyFieldValue) {
              const { IsExpanded, ...rest } = el
              return rest
            }

            const directParent = arr.find((it) => it[keyField] === el[hierarchyField])
            if (directParent?.IsExpanded) {
              const parentIds = getAllParentsIds({
                data: arr,
                hierarchyField,
                keyField,
                keyFieldValue: el[keyField],
              })

              if (parentIds.includes(keyFieldValue)) {
                const { IsExpanded, ...rest } = el
                return rest
              }
            }
            return el
          }
        )
      }
    },
    expandSobAchievementTableRow: (
      state,
      { payload: { keyField, keyFieldValue } }: PayloadAction<IActionTableRowArgs>
    ) => {
      if (state.selectedDocument?.SobAchievement) {
        state.selectedDocument.SobAchievement = state.selectedDocument.SobAchievement.map((el) => {
          if (el[keyField] === keyFieldValue) {
            return { ...el, IsExpanded: 1 }
          }
          return el
        })
      }
    },
    collapseSobAchievementTableRow: (
      state,
      { payload: { keyField, keyFieldValue, hierarchyField } }: PayloadAction<IActionTableRowArgs>
    ) => {
      if (state.selectedDocument?.SobAchievement) {
        state.selectedDocument.SobAchievement = state.selectedDocument.SobAchievement.map((el) => {
          if (el[keyField] === keyFieldValue) {
            const { IsExpanded, ...rest } = el
            return rest
          }
          const directParent = state.selectedDocument?.SobAchievement.find(
            (it) => it[keyField] === el[hierarchyField]
          )
          if (directParent?.IsExpanded) {
            const parentIds = getAllParentsIds({
              data: state.selectedDocument?.SobAchievement,
              hierarchyField,
              keyField,
              keyFieldValue: el[keyField],
            })
            if (parentIds.includes(keyFieldValue)) {
              const { IsExpanded, ...rest } = el
              return rest
            }
          }
          return el
        })
      }
    },

    expandQBRSobCompetitorsTableRow: (
      state,
      {
        payload: { keyField, keyFieldValue, tableType },
      }: PayloadAction<IActionExpandCollapseSobCompetitorsTable>
    ) => {
      const competitors = state.selectedDocument?.SobCompetitors[tableType]
      if (state.selectedDocument && competitors) {
        state.selectedDocument.SobCompetitors[tableType] = expandTableRow<ISobCompetitor>({
          keyFieldValue,
          keyField,
          data: competitors,
        })
      }
    },
    collapseQBRSobCompetitorsTableRow: (
      state,
      {
        payload: { keyField, keyFieldValue, hierarchyField, tableType },
      }: PayloadAction<IActionExpandCollapseSobCompetitorsTable>
    ) => {
      const competitors = state.selectedDocument?.SobCompetitors[tableType]
      if (state.selectedDocument && competitors) {
        state.selectedDocument.SobCompetitors[tableType] = collapseTableRow<ISobCompetitor>({
          keyField,
          keyFieldValue,
          hierarchyField,
          data: competitors,
        })
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(requestGeneratePptxDocument.pending, (state) => {
        state.pptxReportState.error = null
        state.pptxReportState.isGenerating = true
      })
      .addCase(requestGeneratePptxDocument.rejected, (state, action) => {
        if (action.error.message) {
          state.pptxReportState.error = action.error.message
        }
        state.pptxReportState.isGenerating = false
      })
      .addCase(requestGeneratePptxDocument.fulfilled, (state, action) => {
        state.lastModified = new Date()
        state.pptxReportState = action.payload
      })

      .addCase(requestQbrDocument.rejected, (state, action) => {
        if (!action.payload) {
          state.error = TRANSLATION_KEYS.error
        }
      })
      .addCase(requestQbrDocument.fulfilled, (state, action) => {
        const { document, documentPartnerName } = action.payload
        state.documentPartnerName = documentPartnerName

        if (document) {
          openQbrDocumentReducer(state, document)
        }
      })

      .addCase(requestQbrDocumentPartners.rejected, (state, action) => {
        if (!action.payload) {
          state.error = TRANSLATION_KEYS.partnersFetchingError
        }
      })
      .addCase(requestQbrDocumentPartners.fulfilled, (state, action) => {
        state.documentPartners = action.payload
      })

      .addCase(createDraftQbr.pending, (state) => {
        state.isApplyingChanges = true
      })
      .addCase(createDraftQbr.rejected, (state) => {
        state.isApplyingChanges = false
      })
      .addCase(createDraftQbr.fulfilled, (state, action) => {
        const { partners, qbrDraft, documentPartnerName } = action.payload
        state.documentPartners = partners
        state.documentPartnerName = documentPartnerName
        openQbrDocumentReducer(state, qbrDraft)
        state.isApplyingChanges = false
      })

      .addCase(requestResetData.pending, (state) => {
        state.isApplyingChanges = true
        state.unpopulatedFieldsMessages = []
      })
      .addCase(requestResetData.rejected, (state) => {
        state.isApplyingChanges = false
      })
      .addCase(requestResetData.fulfilled, (state, action) => {
        state.isApplyingChanges = false
        const initialData = action.payload
        if (initialData) {
          openQbrDocumentReducer(state, initialData.qbrDocument, true)
          state.documentPartners = initialData.partners
        }
      })

      .addCase(requestApplyChanges.pending, (state) => {
        state.isApplyingChanges = true
      })
      .addCase(requestApplyChanges.rejected, (state) => {
        state.isApplyingChanges = false
      })
      .addCase(requestApplyChanges.fulfilled, (state, action) => {
        state.isApplyingChanges = false
        openQbrDocumentReducer(state, action.payload, true)
      })

      .addCase(requestFinalizeDocumentWithoutSavingChanges.pending, (state) => {
        state.isApplyingChanges = true
      })
      .addCase(requestFinalizeDocumentWithoutSavingChanges.rejected, (state) => {
        state.isApplyingChanges = false
        state.error = TRANSLATION_KEYS.finalizeDocumentError
      })
      .addCase(requestFinalizeDocumentWithoutSavingChanges.fulfilled, (state, action) => {
        state.isApplyingChanges = false
        const initialData = action.payload
        if (initialData) {
          openQbrDocumentReducer(state, initialData.qbrDocument)
          state.documentPartners = initialData.partners
        } else {
          state.isFinalized = true
        }
      })
  },
})

export const {
  changeRTM,
  setQbrEntryId,
  changeUnpopulatedMessages,
  changeQBRDocumentName,
  setUserRole,
  changeQBRCurrency,
  changeQBROfferings,
  changeQBRPartners,
  changeQBRRounding,
  changeQBRNetNdp,
  changeQBRLastStep,
  changeDocumentFormData,
  changeIsLegacyDocument,
  changeQBRNetNdpOptions,
  changeQBRCurrencyOptions,
  removeInitiative,
  changeInitiative,
  addInitiatives,
  resetQbrPptxError,
  openQBRDocument,
  deleteQBRSettingsVersion,
  clearState,
  changeIsSelectedDocumentChanged,
  expandPlanAchievementTableRow,
  collapsePlanAchievementTableRow,
  expandSobAchievementTableRow,
  collapseSobAchievementTableRow,
  collapseQBRSobCompetitorsTableRow,
  expandQBRSobCompetitorsTableRow,
} = QBRSlice.actions

export default QBRSlice.reducer
